import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  Globe, Search, TrendingDown, DollarSign, ArrowRight,
  Plane, ChevronDown, ChevronUp, BarChart3, Info, Heart,
  Share2, Copy, Check, Twitter, Facebook
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useBrand } from '@/config/brands';
import { loadStaticData } from '@/lib/offlineData';

// ─── Types ──────────────────────────────────────────────────────────────────

interface ScenarioCost {
  scenario_id: string;
  country_code: string;
  mapped_cpt_codes: number;
  total_cpt_codes: number;
  intl_total_cost: number;
  us_national_total_cost: number;
}

interface CountryInfo {
  country_code: string;
  country_name: string;
  flag_emoji: string;
  healthcare_system: string;
  currency_code: string;
  default_coverage: string;
  fee_schedule_name: string;
}

interface ScenarioMeta {
  id: string;
  name: string;
  category: string;
  description?: string;
}

// ─── Helpers ────────────────────────────────────────────────────────────────

function formatUSD(amount: number): string {
  if (!amount || !isFinite(amount)) return '$0';
  if (amount >= 1000000) return `$${(amount / 1000000).toFixed(1)}M`;
  if (amount >= 10000) return `$${(amount / 1000).toFixed(0)}k`;
  if (amount >= 1000) return `$${(amount / 1000).toFixed(1)}k`;
  return `$${Math.round(amount).toLocaleString()}`;
}

function savingsPercent(us: number, intl: number): number {
  if (!us || us <= 0) return 0;
  return Math.round(((us - intl) / us) * 100);
}

function savingsColor(pct: number): string {
  if (pct >= 90) return 'text-green-400';
  if (pct >= 70) return 'text-emerald-400';
  if (pct >= 50) return 'text-yellow-400';
  if (pct >= 20) return 'text-orange-400';
  return 'text-red-400';
}

function savingsBg(pct: number): string {
  if (pct >= 90) return 'bg-green-500/10 border-green-500/30';
  if (pct >= 70) return 'bg-emerald-500/10 border-emerald-500/30';
  if (pct >= 50) return 'bg-yellow-500/10 border-yellow-500/30';
  if (pct >= 20) return 'bg-orange-500/10 border-orange-500/30';
  return 'bg-red-500/10 border-red-500/30';
}

// ─── Main Component ─────────────────────────────────────────────────────────

export default function InternationalScenarioCalculator() {
  const brand = useBrand();
  const { t } = useTranslation(['international', 'common']);
  const { uuid, slug } = useParams<{ uuid?: string; slug?: string }>();
  const navigate = useNavigate();
  const isAhp = brand.id === 'ahp';
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [selectedScenarioId, setSelectedScenarioId] = useState<string | null>(uuid || null);
  const [showAllCountries, setShowAllCountries] = useState(false);
  const [copied, setCopied] = useState(false);

  // Sync URL param to state on mount
  useEffect(() => {
    if (uuid && uuid !== selectedScenarioId) {
      setSelectedScenarioId(uuid);
    }
  }, [uuid]);

  // ── Load static data ────────────────────────────────────────────────────
  const { data: intlData, isLoading } = useQuery({
    queryKey: ['international-stats-calculator'],
    queryFn: () => loadStaticData('international_stats.json'),
    staleTime: 1000 * 60 * 60, // 1 hour
  });

  const { data: scenariosData } = useQuery({
    queryKey: ['scenarios-list'],
    queryFn: () => loadStaticData('scenarios.json'),
    staleTime: 1000 * 60 * 60,
  });

  // Navigate to deep link when scenario is selected
  const selectScenario = useCallback((id: string | null) => {
    setSelectedScenarioId(id);
    setShowAllCountries(false);
    if (id) {
      const raw = scenariosData as any;
      const list = Array.isArray(raw) ? raw : raw?.data || raw?.scenarios || [];
      const scenario = list.find((s: any) => (s.uuid || s.id) === id);
      const sSlug = scenario?.slug || id;
      navigate(`/international/calculator/${id}/${sSlug}`, { replace: false });
    } else {
      navigate('/international/calculator', { replace: false });
    }
  }, [navigate, scenariosData]);

  // ── Derived data ────────────────────────────────────────────────────────
  const countries = useMemo(() => {
    if (!intlData?.countries) return [];
    return (intlData.countries as CountryInfo[]).sort((a, b) =>
      a.country_name.localeCompare(b.country_name)
    );
  }, [intlData]);

  const countryMap = useMemo(() => {
    const map: Record<string, CountryInfo> = {};
    countries.forEach(c => { map[c.country_code] = c; });
    return map;
  }, [countries]);

  const scenarioCosts = useMemo(() => {
    if (!intlData?.scenarioCosts) return [];
    return intlData.scenarioCosts as ScenarioCost[];
  }, [intlData]);

  const scenarios = useMemo(() => {
    if (!scenariosData) return [];
    const raw = scenariosData as any;
    const list = Array.isArray(raw) ? raw : raw?.data || raw?.scenarios || [];
    return list.map((s: any) => ({
      id: s.uuid || s.id,
      name: s.name,
      category: s.category,
      description: s.description,
    })) as ScenarioMeta[];
  }, [scenariosData]);

  const categories = useMemo(() => {
    const cats = new Set<string>();
    scenarios.forEach(s => { if (s.category) cats.add(s.category); });
    return Array.from(cats).sort();
  }, [scenarios]);

  // ── Filter scenarios ──────────────────────────────────────────────────
  const filteredScenarios = useMemo(() => {
    let filtered = scenarios;
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(s => s.category === selectedCategory);
    }
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      filtered = filtered.filter(s =>
        s.name.toLowerCase().includes(q) ||
        (s.description && s.description.toLowerCase().includes(q))
      );
    }
    // Only show scenarios that have international cost data
    const scenarioIdsWithCosts = new Set(scenarioCosts.map(sc => sc.scenario_id));
    filtered = filtered.filter(s => scenarioIdsWithCosts.has(s.id));
    return filtered.slice(0, 50); // Limit for performance
  }, [scenarios, selectedCategory, searchQuery, scenarioCosts]);

  // ── Selected scenario comparison ──────────────────────────────────────
  const selectedScenario = useMemo(() => {
    if (!selectedScenarioId) return null;
    return scenarios.find(s => s.id === selectedScenarioId) || null;
  }, [selectedScenarioId, scenarios]);

  const selectedComparison = useMemo(() => {
    if (!selectedScenarioId) return [];
    const costs = scenarioCosts
      .filter(sc => sc.scenario_id === selectedScenarioId && sc.intl_total_cost > 0)
      .map(sc => ({
        ...sc,
        country: countryMap[sc.country_code],
        savings: savingsPercent(sc.us_national_total_cost, sc.intl_total_cost),
        multiplier: sc.us_national_total_cost / sc.intl_total_cost,
      }))
      .filter(sc => sc.country && sc.multiplier > 0 && isFinite(sc.multiplier))
      .sort((a, b) => b.savings - a.savings);
    return costs;
  }, [selectedScenarioId, scenarioCosts, countryMap]);

  const usPrice = selectedComparison.length > 0 ? selectedComparison[0].us_national_total_cost : 0;

  // ── Share helpers (must be after selectedScenario/selectedComparison) ──
  const scenarioSlug = useMemo(() => {
    if (!selectedScenarioId || !scenariosData) return '';
    const raw = scenariosData as any;
    const list = Array.isArray(raw) ? raw : raw?.data || raw?.scenarios || [];
    const s = list.find((s: any) => (s.uuid || s.id) === selectedScenarioId);
    return s?.slug || selectedScenarioId;
  }, [selectedScenarioId, scenariosData]);

  const shareUrl = useMemo(() => {
    if (!selectedScenarioId) return `${brand.primaryDomain}/international/calculator`;
    return `${brand.primaryDomain}/international/calculator/${selectedScenarioId}/${scenarioSlug}`;
  }, [selectedScenarioId, scenarioSlug, brand.primaryDomain]);

  const shareText = useMemo(() => {
    if (!selectedScenario || !selectedComparison?.length) return '';
    const cheapest = selectedComparison[0];
    return `${selectedScenario.name} costs ${formatUSD(usPrice)} in the US but only ${formatUSD(cheapest?.intl_total_cost)} in ${cheapest?.country?.country_name} — that's ${cheapest?.savings}% less! Compare prices across 32 countries:`;
  }, [selectedScenario, selectedComparison, usPrice]);

  const handleCopyLink = useCallback(() => {
    navigator.clipboard.writeText(shareUrl).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }, [shareUrl]);

  const handleShareTwitter = useCallback(() => {
    const url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`;
    window.open(url, '_blank', 'width=550,height=420');
  }, [shareText, shareUrl]);

  const handleShareFacebook = useCallback(() => {
    const url = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`;
    window.open(url, '_blank', 'width=550,height=420');
  }, [shareUrl]);

  // ── Popular scenarios (pre-selected) ──────────────────────────────────
  const popularScenarios = useMemo(() => {
    if (!scenarios.length || !scenarioCosts.length) return [];
    // Find scenarios with the most country coverage and highest US costs
    const scenarioCoverage: Record<string, { count: number; avgUs: number; name: string }> = {};
    scenarioCosts.forEach(sc => {
      if (!scenarioCoverage[sc.scenario_id]) {
        const s = scenarios.find(s => s.id === sc.scenario_id);
        scenarioCoverage[sc.scenario_id] = { count: 0, avgUs: sc.us_national_total_cost, name: s?.name || '' };
      }
      scenarioCoverage[sc.scenario_id].count++;
    });
    return Object.entries(scenarioCoverage)
      .filter(([, v]) => v.count >= 15 && v.avgUs > 500 && v.name)
      .sort((a, b) => b[1].avgUs - a[1].avgUs)
      .slice(0, 12)
      .map(([id, v]) => ({ id, ...v }));
  }, [scenarios, scenarioCosts]);

  // ── Render ────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>
          {selectedScenario
            ? `${selectedScenario.name} — Cost in 32 Countries | ${brand.name}`
            : `What Would This Cost? — International Healthcare Price Calculator | ${brand.name}`
          }
        </title>
        <meta name="description" content={
          selectedScenario && selectedComparison.length > 0
            ? `${selectedScenario.name} costs ${formatUSD(usPrice)} in the US. Compare with ${selectedComparison.length} countries — cheapest is ${selectedComparison[0]?.country?.country_name} at ${formatUSD(selectedComparison[0]?.intl_total_cost)} (${selectedComparison[0]?.savings}% less).`
            : 'Compare US healthcare costs with 32 countries. See how much you could save on surgeries, procedures, and treatments abroad using actual government fee schedules.'
        } />
        <meta property="og:title" content={
          selectedScenario
            ? `${selectedScenario.name}: ${formatUSD(usPrice)} in the US vs ${selectedComparison[0]?.country?.country_name ? formatUSD(selectedComparison[0]?.intl_total_cost) + ' in ' + selectedComparison[0]?.country?.country_name : '32 countries'}`
            : 'What Would This Cost? — Compare Healthcare Prices in 32 Countries'
        } />
        <meta property="og:description" content={
          selectedScenario && selectedComparison.length > 0
            ? `Save up to ${selectedComparison[0]?.savings}% on ${selectedScenario.name}. US avg: ${formatUSD(usPrice)}. Prices from official government fee schedules.`
            : 'Compare US healthcare costs with 32 countries using real government fee schedules.'
        } />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={shareUrl} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={
          selectedScenario
            ? `${selectedScenario.name}: US ${formatUSD(usPrice)} vs ${selectedComparison[0]?.country?.country_name || 'World'} ${selectedComparison[0] ? formatUSD(selectedComparison[0].intl_total_cost) : ''}`
            : 'What Would This Cost? — Healthcare Price Calculator'
        } />
        <meta name="twitter:description" content={
          selectedScenario && selectedComparison.length > 0
            ? `${selectedScenario.name} is ${selectedComparison[0]?.savings}% cheaper in ${selectedComparison[0]?.country?.country_name}. Compare ${selectedComparison.length} countries.`
            : 'Compare healthcare prices across 32 countries.'
        } />
        <link rel="canonical" href={shareUrl} />
        {selectedScenario && (
          <script type="application/ld+json">{JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'WebPage',
            name: `${selectedScenario.name} — International Price Comparison`,
            description: `Compare ${selectedScenario.name} costs across ${selectedComparison.length} countries`,
            url: shareUrl,
            mainEntity: {
              '@type': 'MedicalProcedure',
              name: selectedScenario.name,
              procedureType: selectedScenario.category,
            },
          })}</script>
        )}
      </Helmet>

      {/* Hero */}
      <section className="border-b border-border/40 bg-gradient-to-b from-primary/5 to-background">
        <div className="max-w-7xl mx-auto px-4 py-12 sm:py-16">
          <div className="text-center max-w-3xl mx-auto">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 text-primary text-sm mb-4">
              <Globe className="w-4 h-4" />
              32 Countries · Real Government Fee Schedules
            </div>
            <h1 className="text-3xl sm:text-5xl font-bold tracking-tight mb-4">
              What Would This Cost
              <span className="text-primary"> Anywhere Else?</span>
            </h1>
            <p className="text-lg text-muted-foreground mb-8">
              Select a medical scenario to see what it costs in the US versus 32 countries —
              using actual government fee schedules, not estimates.
            </p>
          </div>

          {/* Search */}
          <div className="max-w-2xl mx-auto">
            <div className="relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
              <input
                type="text"
                placeholder="Search procedures... (e.g., knee replacement, colonoscopy, MRI)"
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                className="w-full pl-12 pr-4 py-4 rounded-xl border border-border bg-card text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 text-lg"
              />
            </div>

            {/* Category pills */}
            <div className="flex flex-wrap gap-2 mt-4 justify-center">
              <button
                onClick={() => setSelectedCategory('all')}
                className={`px-3 py-1.5 rounded-full text-sm transition-colors ${
                  selectedCategory === 'all'
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-muted text-muted-foreground hover:bg-muted/80'
                }`}
              >
                All Categories
              </button>
              {categories.slice(0, 10).map(cat => (
                <button
                  key={cat}
                  onClick={() => setSelectedCategory(cat)}
                  className={`px-3 py-1.5 rounded-full text-sm transition-colors ${
                    selectedCategory === cat
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-muted text-muted-foreground hover:bg-muted/80'
                  }`}
                >
                  {cat}
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {isLoading ? (
          <div className="text-center py-20">
            <Globe className="w-12 h-12 animate-spin text-primary mx-auto mb-4" />
            <p className="text-muted-foreground">Loading international pricing data...</p>
          </div>
        ) : selectedScenarioId && selectedScenario ? (
          /* ─── Comparison Results ──────────────────────────────────────── */
          <div>
            <button
              onClick={() => selectScenario(null)}
              className="text-primary hover:underline mb-6 flex items-center gap-1"
            >
              ← Back to scenarios
            </button>

            {/* US Price Header + Share */}
            <Card className="mb-6 border-red-500/30 bg-red-500/5">
              <CardContent className="p-6">
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                  <div>
                    <p className="text-sm text-muted-foreground mb-1">Selected Scenario</p>
                    <h2 className="text-2xl font-bold">{selectedScenario.name}</h2>
                    <div className="flex items-center gap-2 mt-2">
                      <Badge variant="outline">{selectedScenario.category}</Badge>
                      <Link to={`/scenarios/${selectedScenarioId}/${scenarioSlug}`} className="text-xs text-primary hover:underline">
                        View full scenario →
                      </Link>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-muted-foreground">US National Average</p>
                    <p className="text-4xl font-bold text-red-400">{formatUSD(usPrice)}</p>
                    <p className="text-xs text-muted-foreground mt-1">Based on hospital chargemaster data</p>
                  </div>
                </div>

                {/* Share buttons */}
                <div className="flex items-center gap-2 mt-4 pt-4 border-t border-red-500/20">
                  <span className="text-xs text-muted-foreground mr-1">Share:</span>
                  <Button variant="ghost" size="sm" onClick={handleShareTwitter} className="h-8 px-2 gap-1.5 text-xs">
                    <svg viewBox="0 0 24 24" className="w-3.5 h-3.5" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
                    Post
                  </Button>
                  <Button variant="ghost" size="sm" onClick={handleShareFacebook} className="h-8 px-2 gap-1.5 text-xs">
                    <svg viewBox="0 0 24 24" className="w-3.5 h-3.5" fill="currentColor"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
                    Share
                  </Button>
                  <Button variant="ghost" size="sm" onClick={handleCopyLink} className="h-8 px-2 gap-1.5 text-xs">
                    {copied ? <Check className="w-3.5 h-3.5 text-green-400" /> : <Copy className="w-3.5 h-3.5" />}
                    {copied ? 'Copied!' : 'Copy Link'}
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Scenario Breakdown — what codes make up this scenario */}
            {selectedComparison.length > 0 && selectedComparison[0].total_cpt_codes > 0 && (
              <Card className="mb-6 border-border/40">
                <CardContent className="p-4">
                  <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
                    <BarChart3 className="w-4 h-4 text-primary" />
                    Scenario Breakdown — {selectedComparison[0].total_cpt_codes} CPT codes
                  </h3>
                  <p className="text-xs text-muted-foreground mb-3">
                    This scenario is priced by adding up the national average costs for each included procedure code.{' '}
                    <Link to={`/scenarios/${selectedScenarioId}/${scenarioSlug}`} className="text-primary hover:underline">
                      See full breakdown with all codes →
                    </Link>
                  </p>
                  <div className="flex items-center gap-4 text-xs text-muted-foreground">
                    <span>📋 {selectedComparison[0].total_cpt_codes} total codes in scenario</span>
                    <span>🌍 {selectedComparison[0].mapped_cpt_codes} matched to {selectedComparison[0].country?.country_name || 'this country'}'s system</span>
                    <span className={selectedComparison[0].mapped_cpt_codes < selectedComparison[0].total_cpt_codes ? 'text-yellow-400' : 'text-green-400'}>
                      {Math.round((selectedComparison[0].mapped_cpt_codes / selectedComparison[0].total_cpt_codes) * 100)}% coverage
                    </span>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Country Cards */}
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Globe className="w-5 h-5 text-primary" />
              Same procedure in {selectedComparison.length} countries
            </h3>

            <div className="grid gap-3">
              {(showAllCountries ? selectedComparison : selectedComparison.slice(0, 15)).map((item, idx) => (
                <div
                  key={item.country_code}
                  className={`flex items-center justify-between p-4 rounded-lg border ${savingsBg(item.savings)} transition-all hover:scale-[1.01]`}
                >
                  <div className="flex items-center gap-3">
                    <span className="text-2xl">{item.country?.flag_emoji}</span>
                    <div>
                      <p className="font-medium">{item.country?.country_name}</p>
                      <p className="text-xs text-muted-foreground">
                        {item.country?.healthcare_system} · {item.mapped_cpt_codes}/{item.total_cpt_codes} codes matched
                      </p>
                    </div>
                  </div>
                  <div className="text-right flex items-center gap-6">
                    <div>
                      <p className="text-lg font-bold">{formatUSD(item.intl_total_cost)}</p>
                      <p className="text-xs text-muted-foreground">
                        {item.country?.fee_schedule_name}
                      </p>
                    </div>
                    <div className="min-w-[80px] text-right">
                      <p className={`text-xl font-bold ${savingsColor(item.savings)}`}>
                        {item.savings > 0 ? `-${item.savings}%` : `+${Math.abs(item.savings)}%`}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {item.savings > 0 ? 'savings' : 'more'}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {selectedComparison.length > 15 && !showAllCountries && (
              <button
                onClick={() => setShowAllCountries(true)}
                className="w-full text-center py-3 text-primary hover:underline mt-4"
              >
                Show all {selectedComparison.length} countries ↓
              </button>
            )}

            {/* Key insight */}
            {selectedComparison.length > 0 && (
              <Card className="mt-8 bg-primary/5 border-primary/20">
                <CardContent className="p-6">
                  <div className="flex items-start gap-3">
                    <Plane className="w-6 h-6 text-primary mt-1 shrink-0" />
                    <div>
                      <p className="font-semibold mb-1">Medical Tourism Insight</p>
                      <p className="text-sm text-muted-foreground">
                        The cheapest option is <strong>{selectedComparison[0]?.country?.country_name}</strong> at{' '}
                        <strong>{formatUSD(selectedComparison[0]?.intl_total_cost)}</strong> — that's{' '}
                        <strong className="text-green-400">{selectedComparison[0]?.savings}% less</strong> than
                        the US average of {formatUSD(usPrice)}.
                        You could save <strong>{formatUSD(usPrice - selectedComparison[0]?.intl_total_cost)}</strong>{' '}
                        even after factoring in travel costs.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        ) : (
          /* ─── Scenario Selection ─────────────────────────────────────── */
          <div>
            {/* Popular scenarios */}
            {!searchQuery && selectedCategory === 'all' && popularScenarios.length > 0 && (
              <div className="mb-10">
                <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
                  <TrendingDown className="w-5 h-5 text-primary" />
                  Most Expensive US Procedures
                </h2>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {popularScenarios.map(ps => (
                    <Card
                      key={ps.id}
                      className="cursor-pointer hover:border-primary/50 transition-all hover:scale-[1.02]"
                      onClick={() => selectScenario(ps.id)}
                    >
                      <CardContent className="p-4">
                        <p className="font-medium mb-2 line-clamp-2">{ps.name}</p>
                        <div className="flex items-center justify-between">
                          <p className="text-sm text-muted-foreground">{ps.count} countries</p>
                          <p className="text-lg font-bold text-red-400">{formatUSD(ps.avgUs)}</p>
                        </div>
                        <p className="text-xs text-muted-foreground mt-1">US average → tap to compare</p>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            )}

            {/* All matching scenarios */}
            <h2 className="text-xl font-semibold mb-4">
              {searchQuery || selectedCategory !== 'all'
                ? `${filteredScenarios.length} matching scenarios`
                : 'All Scenarios'
              }
            </h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {filteredScenarios.map(s => {
                const costs = scenarioCosts.filter(sc => sc.scenario_id === s.id);
                const usAvg = costs.length > 0 ? costs[0].us_national_total_cost : 0;
                const cheapest = costs.length > 0
                  ? costs.reduce((min, c) => c.intl_total_cost > 0 && c.intl_total_cost < min ? c.intl_total_cost : min, Infinity)
                  : 0;
                const savings = usAvg > 0 && cheapest < Infinity ? savingsPercent(usAvg, cheapest) : 0;

                return (
                  <Card
                    key={s.id}
                    className="cursor-pointer hover:border-primary/50 transition-all hover:scale-[1.01]"
                    onClick={() => selectScenario(s.id)}
                  >
                    <CardContent className="p-4">
                      <Badge variant="outline" className="text-xs mb-2">{s.category}</Badge>
                      <p className="font-medium mb-3 line-clamp-2">{s.name}</p>
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-muted-foreground">US avg</p>
                          <p className="font-bold text-red-400">{formatUSD(usAvg)}</p>
                        </div>
                        {savings > 0 && (
                          <div className="text-right">
                            <p className="text-sm text-muted-foreground">Best abroad</p>
                            <p className={`font-bold ${savingsColor(savings)}`}>-{savings}%</p>
                          </div>
                        )}
                      </div>
                      <div className="flex items-center justify-between mt-2 text-xs text-muted-foreground">
                        <span>{costs.length} countries</span>
                        <span className="flex items-center gap-1">
                          Compare <ArrowRight className="w-3 h-3" />
                        </span>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>

            {filteredScenarios.length === 0 && (
              <div className="text-center py-12 text-muted-foreground">
                <Search className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>No scenarios found matching "{searchQuery}"</p>
                <p className="text-sm mt-2">Try searching for "knee", "heart", "MRI", or "colonoscopy"</p>
              </div>
            )}
          </div>
        )}

        {/* Bottom CTA */}
        <div className="mt-16 text-center border-t border-border/40 pt-8">
          <p className="text-sm text-muted-foreground mb-4">
            Prices sourced from official government fee schedules across 32 countries.
            <br />
            US prices from hospital chargemaster data ({(intlData as any)?.usPricing?.length?.toLocaleString() || '14,000'}+ procedures).
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            <Link to="/international">
              <Button variant="outline" size="sm">
                <Globe className="w-4 h-4 mr-2" />
                Full Country Comparison
              </Button>
            </Link>
            <Link to="/scenarios">
              <Button variant="outline" size="sm">
                <Heart className="w-4 h-4 mr-2" />
                Browse All Scenarios
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
